import { AfterViewInit, Component, computed, Inject, OnDestroy, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilService } from 'src/app/shared/helper/util.service';
import { ConfirmEmailService } from '../confirm-email/confirm-email.service';
import { emailCheckAsyncValidator } from '../../Validators/checkEmailExists.validator';
import { AuthService } from '@auth0/auth0-angular';
import { gsap } from 'gsap';
import { NgxSpinnerService, Spinner } from 'ngx-spinner';


@Component({
    selector: 'app-confirm-email',
    templateUrl: './confirm-email.component.html',
    styleUrls: ['./confirm-email.component.scss'],
    standalone: false
})
export class ConfirmEmailComponent implements OnInit, AfterViewInit, OnDestroy {


  isVerificationCodeSent = true
  // Set "true" to view forgot password form; 
  // Set "false" to view reset password form;

  // Flag to control whether eye animations should track mouse movement
  isEyeTrackingEnabled = true;


  emailError = "Please Enter valid email"
  showToastMessage = false
  showErrorToast = false;
  error: any = ''
  successMsg = ''
  showSuccessToast = false
  errMsgText: string = null;
  showVerificationSection = false;
  currentDate = ''
  countryName = ''
  operatingSystem = ''
  browserName = ''
  globalEmail: any = '';

  // ==================== COMPONENT STATE PROPERTIES - END ====================

  // ==================== TIMER AND COUNTDOWN PROPERTIES - START ====================
  timer: string = '03:00'; // Initial timer value for 3 minutes;
  private countdownInterval: any;
  isShowTimer: boolean = false;
  codeExpText = "Code Expires in"
  // ==================== TIMER AND COUNTDOWN PROPERTIES - END ====================

  // ==================== FORM PROPERTIES - START ====================
  emailForm: FormGroup;
  verificationForm: FormGroup;
  // ==================== FORM PROPERTIES - END ====================

  // ==================== PASSWORD VISIBILITY PROPERTIES - END ====================

  // ==================== FORM STATE SIGNALS - START ====================
  isLoginForm = signal(false);
  // ==================== FORM STATE SIGNALS - END ====================

  // ==================== CONSTRUCTOR AND DEPENDENCY INJECTION - START ====================
  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    public fb: FormBuilder,
    public cdr: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    public auth: AuthService,
    private spinnerService: NgxSpinnerService,
    private utilService: UtilService,
    private confirmEmail: ConfirmEmailService) {
    this.setFormControls()
    this.activatedRoute.paramMap.subscribe((res: any) => {
      let { id } = res?.params;
    })
  }
  // ==================== CONSTRUCTOR AND DEPENDENCY INJECTION - END ====================

  // ==================== LIFECYCLE METHODS - START ====================
  ngOnInit() {
    let path = this.router.url.split('/')[1];

    this.setFormControls();
    this.currentDate = this.formatDate();
    // this.getCountryName();
    this.countryName = 'India' // temporarly set country for testing
    this.operatingSystem = this.getOS();
    this.browserName = this.getBrowserName();
    this.confirmEmail.email$.subscribe(res => {
      this.globalEmail = res;
    })
  }

  ngAfterViewInit(): void {
    this.initializeEyeAnimations();
  }

  ngOnDestroy(): void {
    clearInterval(this.countdownInterval);
  }
  // ==================== LIFECYCLE METHODS - END ====================

  // ==================== FORM SETUP AND CONFIGURATION - START ====================
  setFormControls() {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,10}$')]]
    })

    this.verificationForm = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(8)]]
    })
  }
  // ==================== FORM SETUP AND CONFIGURATION - END ====================

  // ==================== EYE ANIMATIONS AND INTERACTIONS - START ====================
  private initializeEyeAnimations(): void {
    const eyes = [
      '#shape01_eye',
      '#shape02_left_eye', '#shape02_right_eye',
      '#shape03_left_eye', '#shape03_right_eye',
      '#shape04_left_eye', '#shape04_right_eye',
      '#shape05_left_eye', '#shape05_right_eye',
      '#shape06_left_eye', '#shape06_right_eye',
      '#shape07_left_eye', '#shape07_right_eye'
    ];

    // Group eyes by shape for synchronized blinking
    const eyeGroups = [
      ['#shape01_eye'],
      ['#shape02_left_eye', '#shape02_right_eye'],
      ['#shape03_left_eye', '#shape03_right_eye'],
      ['#shape04_left_eye', '#shape04_right_eye'],
      ['#shape05_left_eye', '#shape05_right_eye'],
      ['#shape06_left_eye', '#shape06_right_eye'],
      ['#shape07_left_eye', '#shape07_right_eye']
    ];

    // Track which shapes are currently blinking
    const blinkingShapes = new Set();

    // Blinking animation function
    const blinkShape = (eyeGroup: string[]) => {
      if (blinkingShapes.has(eyeGroup)) return; // Prevent overlapping blinks

      blinkingShapes.add(eyeGroup);

      eyeGroup.forEach(eyeId => {
        const eye = document.querySelector(eyeId) as SVGCircleElement;
        if (eye) {
          // Store original radius if not already stored
          if (!eye.hasAttribute('data-original-r')) {
            eye.setAttribute('data-original-r', eye.getAttribute('r') || '16');
          }

          const originalRadius = parseFloat(eye.getAttribute('data-original-r') || '16');

          // Blink animation: shrink to 0, then back to original
          gsap.timeline()
            .to(eye, {
              duration: 0.08,
              ease: "power2.inOut",
              attr: { r: 0 }
            })
            .to(eye, {
              duration: 0.08,
              ease: "power2.inOut",
              attr: { r: originalRadius },
              onComplete: () => {
                blinkingShapes.delete(eyeGroup);
              }
            });
        }
      });
    };

    // Start random blinking for all shapes
    const startBlinking = () => {
      eyeGroups.forEach((eyeGroup, index) => {
        const scheduleNextBlink = () => {
          // Random interval between 2-6 seconds
          const randomDelay = Math.floor(Math.random() * 4000) + 2000;

          setTimeout(() => {
            // Check if any other shape is currently blinking
            if (blinkingShapes.size === 0) {
              blinkShape(eyeGroup);
            }
            scheduleNextBlink(); // Schedule the next blink
          }, randomDelay);
        };

        // Start with random initial delay to stagger the first blinks
        setTimeout(() => {
          scheduleNextBlink();
        }, Math.floor(Math.random() * 3000)); // NOSONAR: Safe for UI animation timing
      });
    };

    // Initialize blinking
    startBlinking();

    // Mouse follow animation
    document.addEventListener('mousemove', (e) => {
      // Only track mouse if eye tracking is enabled (not paused for privacy)
      if (!this.isEyeTrackingEnabled) return;

      eyes.forEach(eyeId => {
        const eye = document.querySelector(eyeId) as SVGCircleElement;
        if (eye) {
          // Get the SVG container to calculate relative positions
          const svg = document.querySelector('#eyesAnimation') as SVGSVGElement;
          if (svg) {
            // Get SVG bounding box and mouse position relative to SVG
            const svgRect = svg.getBoundingClientRect();
            const svgMouseX = e.clientX - svgRect.left;
            const svgMouseY = e.clientY - svgRect.top;

            // Scale mouse coordinates to SVG viewBox (800x800)
            const scaleX = 800 / svgRect.width;
            const scaleY = 800 / svgRect.height;
            const mouseX = svgMouseX * scaleX;
            const mouseY = svgMouseY * scaleY;

            // Get original eye position (store if not already stored)
            if (!eye.hasAttribute('data-original-cx')) {
              eye.setAttribute('data-original-cx', eye.getAttribute('cx') || '0');
              eye.setAttribute('data-original-cy', eye.getAttribute('cy') || '0');
            }

            const originalCx = parseFloat(eye.getAttribute('data-original-cx') || '0');
            const originalCy = parseFloat(eye.getAttribute('data-original-cy') || '0');
            const eyeRadius = parseFloat(eye.getAttribute('r') || '16');

            // Calculate direction from original eye position to mouse
            const deltaX = mouseX - originalCx;
            const deltaY = mouseY - originalCy;
            const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            // Maximum movement range for the pupil (should be less than eye socket radius)
            // Assuming eye socket is roughly 3x the pupil size, so max movement is about 1/3 of that
            const maxMove = Math.max(eyeRadius * 0.8, 8); // At least 8 pixels movement

            // Calculate new position (limit movement within the eye socket)
            let moveX = 0;
            let moveY = 0;

            if (distance > 0) {
              const normalizedX = deltaX / distance;
              const normalizedY = deltaY / distance;

              // Limit movement to maxMove radius from original position
              const actualMove = Math.min(maxMove, distance * 0.05);
              moveX = normalizedX * actualMove;
              moveY = normalizedY * actualMove;
            }

            // Calculate final position relative to original position
            const newCx = originalCx + moveX;
            const newCy = originalCy + moveY;

            // Console log for live eye position tracking
            // console.log(`Eye: ${eyeId} | Position: (${newCx.toFixed(2)}, ${newCy.toFixed(2)}) | Original: (${originalCx}, ${originalCy}) | Move: (${moveX.toFixed(2)}, ${moveY.toFixed(2)})`);

            // Animate the eye position using GSAP
            gsap.to(eye, {
              duration: 0.3,
              ease: "power2.out",
              attr: {
                cx: newCx,
                cy: newCy
              }
            });
          }
        }
      });
    });
  }

  // Method to position all eyes to look away when password is visible
  lookAwayForPrivacy(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Disable mouse tracking to pause eye animations
    this.isEyeTrackingEnabled = false;

    // All eye selectors with their exact positions from the console log
    const eyePositions: { [key: string]: { x: number; y: number } } = {
      '#shape01_eye': { x: 404.52, y: 174.14 },
      '#shape02_left_eye': { x: 417.05, y: 387.55 },
      '#shape02_right_eye': { x: 471.12, y: 398.27 },
      '#shape03_left_eye': { x: 606.42, y: 638.07 },
      '#shape03_right_eye': { x: 670.55, y: 629.40 },
      '#shape04_left_eye': { x: 229.58, y: 391.45 },
      '#shape04_right_eye': { x: 276.97, y: 375.61 },
      '#shape05_left_eye': { x: 74.00, y: 492.77 },
      '#shape05_right_eye': { x: 129.24, y: 493.28 },
      '#shape06_left_eye': { x: 172.77, y: 169.59 },
      '#shape06_right_eye': { x: 219.96, y: 190.29 },
      '#shape07_left_eye': { x: 573.16, y: 186.28 },
      '#shape07_right_eye': { x: 624.09, y: 174.85 }
    };

    // Set each eye to its specific position
    Object.entries(eyePositions).forEach(([eyeSelector, position]) => {
      const eye = document.querySelector(eyeSelector) as SVGCircleElement;
      if (eye) {
        // Animate the eye to the exact position
        gsap.to(eye, {
          duration: 0.4,
          ease: "power2.out",
          attr: {
            cx: position.x,
            cy: position.y
          }
        });
      }
    });
  }

  // Method to resume normal mouse tracking
  resumeMouseTracking(): void {
    // Re-enable mouse tracking
    this.isEyeTrackingEnabled = true;

    // Simply trigger the existing mouse tracking logic by simulating a mouse move
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;

    // Create a fake mouse event to reset eyes to center
    const fakeMoveEvent = new MouseEvent('mousemove', {
      clientX: centerX,
      clientY: centerY
    });

    // Dispatch the event to restart normal tracking
    document.dispatchEvent(fakeMoveEvent);
  }
  // ==================== EYE ANIMATIONS AND INTERACTIONS - END ====================


  handlePageVisibility() {
    document.addEventListener('visibilitychange', () => {
    });
  }
  // ==================== UTILITY METHODS - END ====================

  // ==================== FORM STATE MANAGEMENT METHODS - END ====================


  // ==================== FORM GETTERS - START ====================
  get email() {
    return this.emailForm.get('email') as FormControl;
  }

  resendCode(): void {
    this.sendVerification()
  }

  sendVerification() {
    // this.showVerificationSection = true;
    let payload = {
      "email": this.emailForm.get('email').value?.toLowerCase()?.trim(),
      "date": this.currentDate,
      "os": this.operatingSystem,
      "browser": this.browserName,
      "location": this.countryName
    }

    this.confirmEmail.setEmail(this.emailForm.get('email').value?.trim());
    this.confirmEmail.sendEmailVerificationCode(payload).subscribe({
      next: (res: any) => {
        if (res.code == 500) {
          this.error = res.result.error;
          this.showErrorToast = true;
          setTimeout(() => {
            this.showErrorToast = false;
            this.error = ''
          }, 10000)
        }
        else if (res.code == 401) {
          this.error = res.result.error;
          this.showErrorToast = true;
          setTimeout(() => {
            this.showErrorToast = false;
            this.error = ''
          }, 10000)
        }
        else if (res.code == 200) {
          this.successMsg = res.result;
          this.showSuccessToast = true;
          this.showVerificationSection = true;
          this.startCountdown();
          setTimeout(() => {
            this.showSuccessToast = false
            this.successMsg = ''
          }, 5000)
        }
      },
      error: (error) => {
        this.spinnerService.hide()
        this.utilService.SharingDataTextMessage.subscribe((res: any) => {
          this.error = res
        })
        this.showErrorToast = true;
        setTimeout(() => { this.showErrorToast = false; this.error = ''; this.utilService.SharingDataTextMessage.next('') }, 5000)
      }
    }
    )
  }

  startCountdown(): void {
    this.isShowTimer = true;
    if (this.isShowTimer) this.codeExpText = 'Code Expires in';
    let minutes = 3; // Set initial minutes to 3
    let seconds = 0;

    this.countdownInterval = setInterval(() => {
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(this.countdownInterval);
          this.timer = '03:00'; // Reset timer display to 3 minutes
          this.isShowTimer = false;
          if (this.isShowTimer == false) this.codeExpText = 'Code Expired?';
        } else {
          minutes--;
          seconds = 59;
        }
      } else {
        seconds--;
      }

      // Format the timer to always show two digits
      this.timer = `${this.formatTime(minutes)}:${this.formatTime(seconds)}`;
    }, 1000);
  }

  formatTime(time: number): string {
    return time < 10 ? `0${time}` : time.toString();
  }

  formatDate() {
    const options: any = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    };
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const date = new Date();
    const formattedDate = formatter.format(date);
    // Add the timezone information
    return `${formattedDate} (Asia/Kolkata)`;
  }

  getOS() {
    const userAgent = navigator.userAgent;

    let os = "Unknown OS";

    if (userAgent.includes("Windows")) {
      os = "Windows";
    } else if (userAgent.includes("Macintosh")) {
      os = "macOS";
    } else if (userAgent.includes("Linux")) {
      os = "Linux";
    } else if (userAgent.includes("Android")) {
      os = "Android";
    } else if (userAgent.includes("like Mac")) {
      os = "iOS";
    }

    return os;
  }

  getBrowserName() {
    const userAgent = navigator.userAgent;
    let browserName = "Unknown Browser";

    if (userAgent.includes("Chrome")) {
      browserName = "Google Chrome";
    } else if (userAgent.includes("Firefox")) {
      browserName = "Mozilla Firefox";
    } else if (userAgent.includes("Safari")) {
      browserName = "Apple Safari";
    } else if (userAgent.includes("MSIE") || userAgent.includes("Trident")) {
      browserName = "Microsoft Internet Explorer";
    } else if (userAgent.includes("Edge")) {
      browserName = "Microsoft Edge";
    } else if (userAgent.includes("Opera") || userAgent.includes("OPR")) {
      browserName = "Opera";
    }

    return browserName;
  }

  verifyCode() {
    let payload = {
      "email": this.globalEmail.toLowerCase()?.trim(),
      "verificationCode": this.verificationForm.get('code').value
    }
    // this.router.navigate(['/create-password'])
    this.confirmEmail.verifyEmailCode(payload).subscribe((res: any) => {
      if (res.code == 200) {
        this.isShowTimer = false;
        if (this.isShowTimer == false) this.codeExpText = 'Code Expired?';
        this.showToastMessage = true;
        this.verificationForm.get('code').patchValue('')
        setTimeout(() => { this.showToastMessage = false; this.showSuccessToast = false; this.router.navigateByUrl('/create-password') }, 3000)
      } else if (res.code == 500) {
        this.error = res.result.error;
        this.showErrorToast = true;
        setTimeout(() => {
          this.showErrorToast = false;
          this.error = ''
        }, 5000)
      }
    }, error => {
      this.spinnerService.hide()
      this.utilService.SharingDataTextMessage.subscribe((res: any) => {
        this.error = res
      })
      if (this.error == 'Error Verification code has Expired') {
        this.isShowTimer = false;
        this.codeExpText = 'Code Expired?'
      }
      this.showErrorToast = true;
      setTimeout(() => { this.showErrorToast = false; this.error = ''; this.utilService.SharingDataTextMessage.next('') }, 5000)

    })
  }

}
